import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { db } from '../offline/db';
import { useOnlineStatus } from '../offline/ConnectivityContext';
import { apiLogin, apiLogout, apiMe } from './authApi';
import { AUTHENTICATED_EVENT, UNAUTHORIZED_EVENT } from './installAuthFetch';
import { clearHomeData } from '../utils/recentBuildings';

// Session utilisateur côté front (AUTH_PLAN.md §5.2-5.5).
// - Au démarrage : GET /auth/me → { auth_enabled, user }.
// - Hors-ligne : dernier `me` connu (localStorage, aucun secret) pour que l'app s'ouvre sur le terrain.
// - 401 sur un appel à l'API (walter:unauthorized) : modale de reconnexion par-dessus la page,
//   sans redirection ; une fois reconnecté, walter:authenticated relance la file hors-ligne.
// - Tant que l'API est en AUTH_ENABLED=false, rien n'est imposé (AuthGate laisse tout passer).
// AuthGate (garde + modale de reconnexion) se place sous ce provider, autour du router.

const ME_CACHE_KEY = 'walter_me';

export const STAFF_ROLES = ['admin', 'auditeur'];
export const ROLE_LABELS = {
    admin: 'Administrateur',
    auditeur: 'Auditeur Hydros',
    client_editeur: 'Client — éditeur',
    client_lecteur: 'Client — lecteur',
};

const AuthContext = createContext(null);

function readCache() {
    try {
        return JSON.parse(localStorage.getItem(ME_CACHE_KEY)) || null;
    } catch (error) {
        return null;
    }
}

function writeCache(value) {
    try {
        localStorage.setItem(ME_CACHE_KEY, JSON.stringify(value));
    } catch (error) {
        // Stockage indisponible (navigation privée…) : l'app marche, sans démarrage hors-ligne.
    }
}

// Données locales d'un compte (copie hors-ligne des audits, données de l'accueil),
// effacées à la déconnexion et quand un autre compte se connecte sur l'appareil.
async function clearOfflineData() {
    await db.transaction('rw', db.tables, () => Promise.all(db.tables.map((table) => table.clear())));
    clearHomeData();
}

export function AuthProvider({ children }) {
    const { isOnline } = useOnlineStatus();
    const [state, setState] = useState({ status: 'loading', user: null, authEnabled: false, fromCache: false });
    const [reconnectOpen, setReconnectOpen] = useState(false);
    const stateRef = useRef(state);
    stateRef.current = state;

    const applySession = useCallback(async (user, authEnabled) => {
        const previous = readCache();
        // Appareil partagé : quand un autre compte se connecte et que rien n'attend d'être
        // synchronisé, la copie locale des audits du compte précédent est effacée.
        if (user && previous?.lastUserId && previous.lastUserId !== user._id && (await db.syncQueue.count()) === 0) {
            await clearOfflineData();
        }
        writeCache({ user, authEnabled, lastUserId: user?._id || previous?.lastUserId || null });
        setState({ status: 'ready', user, authEnabled, fromCache: false });
    }, []);

    const refresh = useCallback(async () => {
        try {
            const me = await apiMe();
            await applySession(me.user, Boolean(me.auth_enabled));
        } catch (error) {
            const cached = readCache();
            setState({
                status: 'ready',
                user: cached?.user || null,
                authEnabled: Boolean(cached?.authEnabled),
                fromCache: true,
            });
        }
    }, [applySession]);

    useEffect(() => { refresh(); }, [refresh]);

    // Démarré hors-ligne sur le cache : revalide la session dès que l'API répond.
    useEffect(() => {
        if (isOnline && stateRef.current.fromCache) refresh();
    }, [isOnline, refresh]);

    useEffect(() => {
        const handleUnauthorized = () => setReconnectOpen(true);
        window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
        return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    }, []);

    // Réponse de /auth/login, /auth/accept_invite ou /auth/reset_password.
    const acceptSession = useCallback(async (result) => {
        await applySession(result.user, Boolean(result.auth_enabled));
        setReconnectOpen(false);
        window.dispatchEvent(new Event(AUTHENTICATED_EVENT));
        return result.user;
    }, [applySession]);

    const login = useCallback(
        async (email, password) => acceptSession(await apiLogin(email, password)),
        [acceptSession]
    );

    // Bloquée tant que des écritures faites hors-ligne attendent : se déconnecter les
    // rendrait impossibles à envoyer sous ce compte.
    const logout = useCallback(async () => {
        const pending = await db.syncQueue.count();
        if (pending > 0) return { blocked: pending };
        await apiLogout();
        await clearOfflineData();
        writeCache({ user: null, authEnabled: stateRef.current.authEnabled, lastUserId: null });
        setState((current) => ({ ...current, user: null, fromCache: false }));
        return { ok: true };
    }, []);

    const value = useMemo(() => ({
        ...state,
        isAdmin: state.user?.role === 'admin',
        isStaff: STAFF_ROLES.includes(state.user?.role),
        login,
        acceptSession,
        logout,
        refresh,
        reconnectOpen,
        openLogin: () => setReconnectOpen(true),
        closeLogin: () => setReconnectOpen(false),
    }), [state, reconnectOpen, login, acceptSession, logout, refresh]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    return useContext(AuthContext);
}
